import React, { ReactElement, useEffect, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import {
    Comparison, FieldTypedValue, FormField, FormFieldType, WorkflowConditionMapping,
} from "../../../__generated__/api";
import FieldTypeValueDisplay from "../../form-field/FieldTypeValueDisplay";
import FieldSelector from "../../pickers/field-selector/FieldSelector";
import FieldType from "../../pickers/field-type/FieldType";
import Select from "../../select/Select";
import { ActionProps } from "./base";
import { COMPARISON_OPTIONS } from "./condition";

const FORM_FIELD_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,map)";

const FORM_FIELD_OUTPUT_ID = "urn:fq:workflow-service-action-output:(urn:fq:workflow-service-action:"
                              + "(urn:fq:workflow-service:system,map),value)";

export default function MapSettings(props: ActionProps): ReactElement {
    const [lastValue, setValue] = useState<string>();
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [mapGroups, setMapGroups] = useState<WorkflowConditionMapping>(() => ({
        mapping: [
            {
                conditions: [
                    {
                        comparison: Comparison.Equal,
                        fieldId: "",
                    },
                ],
                value: {
                    fieldId: "",
                    type: FormFieldType.Text,
                },
            },
        ],
        valueType: FormFieldType.Text,
    }));
    const flatTypes = props.input.flatMap(row => row.input?.map(field => ({
        field,
        group: row,
    }))).filterNull();

    useEffect(() => {
        const serialized = JSON.stringify(mapGroups);
        setValue(serialized);
        const condition = mapGroups.mapping.firstElement.conditions.map(group => {
            const field = flatTypes.find(type => type.field.id === group.fieldId)?.field.name;
            const comparison = COMPARISON_OPTIONS[group.comparison as Comparison];
            return `${field} ${comparison.text} ${group.value?.displayValue}`;
        }).join(" AND ");
        if (!isDirty) {
            return;
        }
        props.onChange({
            values: [
                {
                    fieldId: FORM_FIELD_ID,
                    type: FormFieldType.LongText,
                    displayValue: condition,
                    string: serialized,
                },
            ],
            output: [{
                id: FORM_FIELD_OUTPUT_ID,
                name: "Value",
                description: "The output of the mapping step",
                type: mapGroups.valueType,
            }],
        });
    }, [mapGroups]);

    useEffect(() => {
        if (props.values?.[0]?.value?.string && lastValue !== props.values[0].value.string) {
            setMapGroups(JSON.parse(props.values[0].value?.string));
        }
    }, [props.values?.[0]?.value?.string]);

    const addGroup = () => {
        const mapping = Array.from(mapGroups.mapping);
        mapping.push({
            conditions: [
                {
                    comparison: Comparison.Equal,
                    fieldId: "",
                },
            ],
            value: {
                fieldId: "",
                type: FormFieldType.Text,
            },
        });
        setIsDirty(true);
        setMapGroups({
            mapping,
            valueType: mapGroups.valueType,
        });
    };
    const addCondition = (groupIndex: number) => {
        const mapping = Array.from(mapGroups.mapping);
        mapping[groupIndex].conditions.push({
            comparison: Comparison.Equal,
            fieldId: "",
        });
        setIsDirty(true);
        setMapGroups({
            mapping,
            valueType: mapGroups.valueType,
        });
    };
    const deleteCondition = (groupIndex: number, conditionIndex: number) => {
        const mapping = Array.from(mapGroups.mapping);
        mapping[groupIndex].conditions.splice(conditionIndex, 1);
        if (mapping[groupIndex].conditions.isEmpty) {
            mapping.splice(groupIndex, 1);
        }
        if (mapping.isEmpty) {
            mapping.push({
                conditions: [
                    {
                        comparison: Comparison.Equal,
                        fieldId: "",
                    },
                ],
                value: {
                    fieldId: "",
                    type: FormFieldType.Text,
                },
            });
        }
        setIsDirty(true);
        setMapGroups({
            mapping,
            valueType: mapGroups.valueType,
        });
    };

    return (
        <div className="list-group list-flush">
            <div className="d-flex w-100 mb-4">
                <div className="col-3">
                    Value Type
                </div>

                <div className="col-3">
                    <FieldType
                        onChange={value => {
                            setIsDirty(true);
                            setMapGroups({
                                mapping: mapGroups.mapping,
                                valueType: value,
                            });
                        }}
                        value={mapGroups.valueType}
                        allowedTypes={[
                            FormFieldType.Text,
                            FormFieldType.LongText,
                            FormFieldType.Number,
                            FormFieldType.Date,
                            FormFieldType.Boolean,
                            FormFieldType.Date,
                            FormFieldType.Time,
                            FormFieldType.DateTime,
                            FormFieldType.Decimal,
                            FormFieldType.User,
                            FormFieldType.Users,
                        ]}
                    />
                </div>
            </div>

            {mapGroups.mapping.map((conditionGroup, groupIndex) => (
                <div
                    className="list-group-item bg-gray-200 mb-3"
                    // eslint-disable-next-line react/no-array-index-key
                    key={`orGroup-${groupIndex}`}
                >
                    <div>
                        If
                    </div>
                    {conditionGroup.conditions.map((condition, index) => {
                        const input = flatTypes.find(row => (!condition.stepId || condition.stepId === row.group.id)
                             && row.field.id === condition.fieldId);
                        const comparisons = Object.entries(COMPARISON_OPTIONS)
                            .filter(([, value]) => !value.allowedTypes
                                        || (input?.field.type && value.allowedTypes.has(input?.field.type)));
                        const conditionLength = conditionGroup.conditions.length;
                        const isLast = index === conditionLength - 1;
                        const isOnlyOne = mapGroups.mapping.length === 1 && conditionLength === 1;
                        const selectedCondition = comparisons.find(([key]) => key === condition.comparison);
                        const conditionValue = condition?.value?.string as string;
                        return (
                            <div
                                // eslint-disable-next-line react/no-array-index-key
                                key={`condition-${groupIndex}-${index}`}
                                className={`d-flex ${index > 0 ? "border-top pt-2 mt-2 ms-3" : "ms-3"}`}
                            >
                                <div className="col">
                                    <FieldSelector
                                        onChange={value => {
                                            const mapping = Array.from(mapGroups.mapping);
                                            if (value) {
                                                mapping[groupIndex].conditions[index] ??= {};
                                                mapping[groupIndex].conditions[index].fieldId = value.field.id;
                                                mapping[groupIndex].conditions[index].stepId = value.group.id;
                                            }
                                            setIsDirty(true);
                                            setMapGroups({
                                                mapping,
                                                valueType: mapGroups.valueType,
                                            });
                                        }}
                                        value={input}
                                        input={props.input}
                                    />
                                </div>
                                <div className="col">
                                    {condition.fieldId && (
                                        <Select
                                            onChange={value => {
                                                const mapping = Array.from(mapGroups.mapping);
                                                mapping[groupIndex].conditions[index] ??= {};
                                                mapping[groupIndex].conditions[index].comparison = value.value as Comparison;
                                                setIsDirty(true);
                                                setMapGroups({
                                                    mapping,
                                                    valueType: mapGroups.valueType,
                                                });
                                            }}
                                            value={!selectedCondition ? undefined : {
                                                label: selectedCondition[1].text,
                                                value: selectedCondition[0],
                                            }}
                                            options={comparisons.map(([value, options]) => ({
                                                label: options.text,
                                                value,
                                            }))}
                                        />
                                    )}

                                </div>
                                <div className="col">
                                    {input
                                            && condition.comparison !== Comparison.Exists
                                             && condition.comparison !== Comparison.DoesntExist
                                             && condition.comparison !== Comparison.Contains
                                             && condition.comparison !== Comparison.DoesntContain
                                             && condition.comparison !== Comparison.Regexp
                                             && (
                                                 <FieldTypeValueDisplay
                                                     className=""
                                                     inputClassName="form-control"
                                                     field={input.field as FormField}
                                                     shouldShowFieldName={false}
                                                     value={condition.value as FieldTypedValue}
                                                     onChange={value => {
                                                         const mapping = Array.from(mapGroups.mapping);
                                                         mapping[groupIndex].conditions[index].value = {
                                                             ...value,
                                                             fieldId: FORM_FIELD_ID,
                                                         };
                                                         setIsDirty(true);
                                                         setMapGroups({
                                                             mapping,
                                                             valueType: mapGroups.valueType,
                                                         });
                                                     }}
                                                 />
                                             )}

                                    {input && (
                                        condition.comparison === Comparison.Contains
                                                || condition.comparison === Comparison.DoesntContain
                                                || condition.comparison === Comparison.Regexp
                                    ) && (
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={conditionValue}
                                            onChange={evt => {
                                                const mapping = Array.from(mapGroups.mapping);
                                                mapping[groupIndex].conditions[index].value = {
                                                    displayValue: evt.target.value,
                                                    fieldId: input.field.id,
                                                    type: input.field.type,
                                                    string: evt.target.value,
                                                };
                                                setIsDirty(true);
                                                setMapGroups({
                                                    mapping,
                                                    valueType: mapGroups.valueType,
                                                });
                                            }}
                                        />
                                    )}

                                </div>
                                <div className="col-2 d-flex justify-content-end">
                                    {isLast && (
                                        <button
                                            type="button"
                                            className="btn btn-none"
                                            onClick={() => addCondition(groupIndex)}
                                        >
                                            AND
                                        </button>
                                    )}
                                    {!(isOnlyOne && isLast) && (
                                        <button
                                            type="button"
                                            className="btn btn-none"
                                            onClick={() => deleteCondition(groupIndex, index)}
                                        >
                                            <FiTrash2 />
                                        </button>
                                    )}

                                </div>
                            </div>

                        );
                    })}
                    <div className="border-top mt-1 pt-1">
                        <div>
                            Then set value to:
                        </div>
                        <div className="ms-3">
                            <FieldTypeValueDisplay
                                className=""
                                inputClassName="form-control"
                                field={{
                                    id: "value",
                                    isSystemField: false,
                                    name: "Value",
                                    type: mapGroups.valueType,
                                    isRequired: true,
                                } as FormField}
                                shouldShowFieldName={false}
                                value={conditionGroup.value as FieldTypedValue}
                                onChange={value => {
                                    const mapping = Array.from(mapGroups.mapping);
                                    mapping[groupIndex].value = {
                                        ...value,
                                        fieldId: FORM_FIELD_OUTPUT_ID,
                                    };
                                    setIsDirty(true);
                                    setMapGroups({
                                        mapping,
                                        valueType: mapGroups.valueType,
                                    });
                                }}
                            />
                        </div>

                    </div>
                </div>
            ))}
            <div className="">

                <button type="button" className="btn btn-outline mt-2" onClick={addGroup}>
                    Add Mapping
                </button>

            </div>
        </div>
    );
}
